import { SegmentedControl, Text } from "@mantine/core";
import { useMediaQuery } from "@mui/material";
import { useMemo } from "react";
import { CustomStripePrice } from "../../../types/product-schema";
import { useMembershipFilterStyles } from "./BillingPeriodSelector";

export default function CurrencySelector({
  selectedCurrency,
  onCurrencyChange,
  referencePrices,
  disabled = false,
}: {
  selectedCurrency: string;
  onCurrencyChange: (currency: "gbp" | "usd" | "eur") => void;
  referencePrices: CustomStripePrice[];
  disabled?: boolean;
}) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { classes } = useMembershipFilterStyles();

  const availableCurrencies = useMemo(() => {
    const currencySet = new Set<string>();

    referencePrices.forEach((price) => {
      currencySet.add(price.currency);
    });

    const preferredOrder = ["gbp", "usd", "eur"];
    const sortedCurrencies = Array.from(currencySet).sort((a, b) => {
      const indexA = preferredOrder.indexOf(a);
      const indexB = preferredOrder.indexOf(b);

      if (indexA === -1 && indexB === -1) return a.localeCompare(b);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });

    return sortedCurrencies.map((currency) => ({
      label: (
        <Text className={classes.periodSelectorLabel}>
          {getCurrencySymbol(currency)}
        </Text>
      ),
      value: currency,
    }));
  }, [referencePrices, classes.periodSelectorLabel]);

  return (
    <SegmentedControl
      size="sm"
      data={availableCurrencies}
      value={selectedCurrency}
      onChange={(value) => onCurrencyChange(value as "gbp" | "usd" | "eur")}
      className={classes.segmentedControl}
      fullWidth={isMobile}
      disabled={disabled}
    />
  );
}

function getCurrencySymbol(currency: string) {
  switch (currency) {
    case "gbp":
      return "£";
    case "usd":
      return "$";
    case "eur":
      return "€";
  }
}
